import { useEffect, useRef } from 'react';
import { catalogApi } from '../services/api';

/**
 * Minimum term length before a zero-result search is worth logging.
 * Mirrors the server-side floor (SEARCH_MISS_MIN_TERM_LENGTH) — shorter terms
 * are accidental keystrokes, not real "plant not found" signals.
 */
export const SEARCH_MISS_MIN_TERM_LENGTH = 3;

/**
 * Extra settle delay (ms) after the search query stabilizes before reporting.
 * The catalog header already debounces typing by 300ms; this second, longer
 * pause ensures we only log a term the user has actually finished typing — not
 * the intermediate substrings a slow typer produces ("ban" → "banan" → ...).
 */
export const SEARCH_MISS_SETTLE_MS = 1000;

/**
 * Report anonymous catalog search misses ("plant searched, not found", #593).
 *
 * A "miss" is logged only when the search term **alone** matches zero plants in
 * the catalog — sidebar filters are deliberately ignored, so a filter-induced
 * empty result ("Rose" + "cat-safe" = 0, but roses exist) is never mistaken for
 * a missing plant. This keeps the statistic a clean signal of real DB gaps for
 * the retention-driven expansion (#541).
 *
 * DSGVO-Design: fires once per distinct term per session (deduped), after a
 * settle pause, and only the normalized term is sent — no user/session/IP.
 *
 * @param {Object} params
 * @param {string} params.searchQuery - Current (already header-debounced) query
 * @param {number} params.searchMatchCount - Plants matching the term by name
 *   alone (search-only, ignoring filters). 0 means a true miss.
 * @param {boolean} [params.enabled] - Gate reporting until the catalog has
 *   loaded (an empty catalog would report a spurious 0-match for every term).
 */
export function useSearchMissReporter({ searchQuery, searchMatchCount, enabled = true }) {
  // Terms already reported this session — avoids double-counting one view.
  const reportedRef = useRef(new Set());
  const timerRef = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;

    const term = (searchQuery || '').trim();
    // Only a genuine DB gap: long enough AND zero name matches in the catalog.
    if (term.length < SEARCH_MISS_MIN_TERM_LENGTH || searchMatchCount !== 0) {
      return undefined;
    }

    const key = term.toLowerCase();
    if (reportedRef.current.has(key)) return undefined;

    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      // Mark before sending so a re-render during the request can't double-fire.
      reportedRef.current.add(key);
      catalogApi.reportSearchMiss(term);
    }, SEARCH_MISS_SETTLE_MS);

    return () => clearTimeout(timerRef.current);
  }, [searchQuery, searchMatchCount, enabled]);
}

export default useSearchMissReporter;
